import type { USD, WEIString, } from "@bracket/types";
import type { MorphoMarketPositionValues, LoadedVault, } from "@types";
import type { GetMarketPositionsProps, } from "./getMarketPosition";
import { getMarketPositions, } from "./getMarketPositions";
import type { MorphoRollupResult, } from "./types";

export declare type GetMorphoForAllVaultsProps = GetMarketPositionsProps;

export const getMorphoForAllVaults = async ({
	wallet,
	vaults,
	force = false,
}: GetMorphoForAllVaultsProps): Promise<MorphoRollupResult> => {

	if(gathering) {
		return returnGathered();
	}

	gathering = true;

	const result = await getMarketPositions({ wallet, vaults, force, });
	data = rollup(result, vaults);
	gathering = false;
	return data;

}

let gathering = false;
let data: MorphoRollupResult|null = null;

const returnGathered = (): Promise<MorphoRollupResult> => {
	return new Promise((resolve, reject) => {
		const to = setTimeout(() => {
			reject({ all: null, vaults: null, ready: false, error: new AggregateError([new Error("Morpho rollup timed out")]), });
		}, 10_000);
		const inte = setInterval(() => {
			if(data) {
				clearTimeout(to);
				clearInterval(inte);
				resolve(data);
			}
		}, 25);
	});
}


const rollup = ( data: Record<string, MorphoMarketPositionValues|null>|null, vaults: LoadedVault[] ):MorphoRollupResult => {

	if(!data) {
		return { all: null, vaults: null, ready: false, };
	}

	const usedVaults = vaults.filter(v => data[v.symbol] !== null);

	const average = <T extends object>( values: Array<{ asset?: WEIString; usd?: number; perc?: number; }> ): T => {
		const hasUsd = values.some(v => v.usd !== undefined);
		const hasPerc = values.some(v => v.perc !== undefined);
		const usd = hasUsd ? values.reduce((acc, v) => acc + (v.usd || 0), 0) / values.length : undefined;
		const perc = hasPerc ? values.reduce((acc, v) => acc + (v.perc || 0), 0) / values.length || -1 : undefined;
		return Object.assign({} as T, hasPerc?{ perc, } : {}, hasUsd?{ usd, } : {});
	}

	const max = <T extends object>( values: Array<{ asset?: WEIString; usd?: number; perc?: number; }> ): T => {
		const hasUsd = values.some(v => v.usd !== undefined);
		const hasPerc = values.some(v => v.perc !== undefined);
		const usd = hasUsd ? values.reduce((acc, v) => typeof v.usd === "number" && acc < v.usd ? v.usd : acc, 0) : undefined;
		const perc = hasPerc ? values.reduce((acc, v) => typeof v.perc === "number" && acc < v.perc ? v.perc : acc, 0) : undefined;
		return Object.assign({} as T, hasPerc?{ perc, } : {}, hasUsd?{ usd, } : {});
	}

	const min = <T extends object>( values: Array<{ asset?: WEIString; usd?: number; perc?: number; }> ): T => {
		const hasUsd = values.some(v => v.usd !== undefined);
		const hasPerc = values.some(v => v.perc !== undefined);
		const usd = hasUsd ? values.reduce((acc, v) => typeof v.usd === "number" && acc > v.usd ? v.usd : acc, -Infinity) : undefined;
		const perc = hasPerc ? values.reduce((acc, v) => typeof v.perc === "number" && acc > v.perc ? v.perc : acc, Infinity) : undefined;
		return Object.assign({} as T, hasPerc?{ perc, } : {}, hasUsd?{ usd, } : {});
	}

	const weightedAverage = (items: { value: number; weight: number; }[]): { perc: number; } => {
		let sumWeightedValues = 0;
		let sumWeights = 0;
		for(const item of items) {
			sumWeightedValues += item.value * item.weight;
			sumWeights += item.weight;
		}
		const result = sumWeights !== 0 ? sumWeightedValues / sumWeights : 0;
		return { perc: result };
	}

	const averagePercent = (values: Array<{ asset?: WEIString; usd?: number; perc?: number; } | null>): { asset: WEIString; usd: USD; perc: number; } | null => {
		const validValues = values.filter((v): v is { perc?: number; } => v !== null && v !== undefined && typeof v.perc === "number");
		if(validValues.length === 0) return null;
		const perc = validValues.reduce((acc, v) => acc + (v.perc || 0), 0) / validValues.length;
		return { asset: BigInt("-1").toString(), usd: -1, perc, };
	}


	const sum = <T extends object>( values: Array<{ asset?: WEIString; usd?: number; perc?: number; }> ): T => {
		const hasUsd = values.some(v => v.usd !== undefined);
		const hasPerc = values.some(v => v.perc !== undefined);
		const usd = hasUsd ? values.reduce((acc, v) => acc + (v.usd || 0), 0) : undefined;
		const perc = hasPerc ? values.reduce((acc, v) => acc + (v.perc || 0), 0) : undefined;
		return Object.assign({} as T, hasPerc?{ perc, } : {}, hasUsd?{ usd, } : {});
	}
	// const chk = average<MorphoMarketPositionValues["effectiveYield"]>	(Object.values(data).map(v => v?.effectiveYield || null).filter(v => v !== null));

	const result:MorphoRollupResult = {
		all: {
			morphoApy: average<MorphoMarketPositionValues["morphoApy"]>(Object.values(data).map(v => v?.morphoApy || null).filter(v => v !== null)),
			morphoCollateral: average<MorphoMarketPositionValues["morphoCollateral"]>(Object.values(data).map(v => v?.morphoCollateral || null).filter(v => v !== null)),
			morphoBorrow: average<MorphoMarketPositionValues["morphoBorrow"]>(Object.values(data).map(v => v?.morphoBorrow || null).filter(v => v !== null)),
			morphoBorrowApy: weightedAverage(Object.entries(data).map(([k, v]) => ({ value: v?.morphoApy?.perc ?? 0, weight: data[k]?.morphoBorrow?.usd ?? 0})).filter((v): v is { value: number; weight: number; } => typeof v.value === "number" && typeof v.weight === "number" && v.weight > 0)),
			morphoEquity: average<MorphoMarketPositionValues["morphoEquity"]>(Object.values(data).map(v => v?.morphoEquity || null).filter(v => v !== null)),
			vaultEarn: average<MorphoMarketPositionValues["vaultEarn"]>(Object.values(data).map(v => v?.vaultEarn || null).filter(v => v !== null)),
			borrowCost: average<MorphoMarketPositionValues["borrowCost"]>(Object.values(data).map(v => v?.borrowCost || null).filter(v => v !== null)),
			earnings: average<MorphoMarketPositionValues["earnings"]>(Object.values(data).map(v => v?.earnings || null).filter(v => v !== null)),
			equity: sum<MorphoMarketPositionValues["equity"]>(Object.values(data).map(v => v?.equity || null).filter(v => v !== null)),
			effectiveYield: average<MorphoMarketPositionValues["effectiveYield"]>	(Object.values(data).map(v => v?.effectiveYield || null).filter(v => v !== null)),
			leverage: average<MorphoMarketPositionValues["leverage"]>(Object.values(data).map(v => v?.leverage || null).filter(v => v !== null)),
			maxLeverage: max<MorphoMarketPositionValues["leverage"]>(Object.values(data).map(v => v?.leverage || null).filter(v => v !== null)),
			healthFactor: averagePercent(Object.values(data).map(v => v?.healthFactor ?? null)),
			priceVariationToLiquidationPrice: averagePercent(Object.values(data).map(v => v?.priceVariationToLiquidationPrice ?? null)),
			timestamp: Date.now(),
		},
		vaults: data,
		ready: true,
	}

	return result;

}
